import React, { useState, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { format, isBefore, addDays } from "date-fns";
import { vi } from "date-fns/locale";
import { useExcelExport } from "@/hooks/useExcelExport";
import { downloadExcelTemplate, exportToExcel, type ExcelColumnDef } from "@/lib/excel.utils";
import { ExportImportButtons } from "@/components/shared/ExportImportButtons";
import { trpcClient } from "@/lib/trpc";
import { mapProductionOrderListItem } from "@/lib/transformers/production-orders";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { PageFilterTags } from "@/components/shared/PageFilterTags";
import { type StatusSidebarItem } from "@/components/shared/StatusSidebar";
import { ProductionOrderStatus } from "@/config/status-registry";
import {
  ListPageShell,
  useServerListFilter,
} from "@/components/shared/ListPageShell";
import { ResponsiveDataView } from "@/components/shared/ResponsiveDataView";
import type { FilterField } from "@/components/shared/FilterBar";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import { useRowSelection } from "@/hooks/useRowSelection";
import {
  DataTableRow,
  DataTableRowCheckbox,
  DataTableSelectAllCheckbox,
} from "@/components/shared/DataTableRow";
import { BulkActionBar } from "@/components/shared/BulkActionBar";

const STATUS_ITEMS: StatusSidebarItem[] = [
  { key: "PLANNED", label: "Đã lên kế hoạch", color: "bg-blue-100 text-blue-800", dotColor: "bg-blue-500" },
  { key: "READY_FOR_SCHEDULING", label: "Sẵn sàng SX", color: "bg-purple-100 text-purple-800", dotColor: "bg-purple-500" },
  { key: "IN_PROGRESS", label: "Đang sản xuất", color: "bg-amber-100 text-amber-800", dotColor: "bg-amber-500" },
];

const EXPORT_COLUMNS: ExcelColumnDef[] = [
  { key: "lsxId", label: "Mã LSX", width: 14 },
  { key: "productName", label: "Sản phẩm", width: 25 },
  { key: "quantity", label: "Số lượng", width: 14, format: "number" },
  { key: "status", label: "Trạng thái", width: 16 },
  { key: "materialStatus", label: "Vật tư", width: 16 },
  { key: "toolingStatus", label: "Khuôn", width: 16 },
];

const FILTER_FIELDS: FilterField[] = [
  { key: "orderId", label: "Mã LSX", type: "text", placeholder: "LSX-..." },
  {
    key: "productName",
    label: "Sản phẩm",
    type: "text",
    placeholder: "Tên SP...",
  },
  {
    key: "materialStatus",
    label: "Trạng thái Vật Tư",
    type: "combobox-multi",
    options: [
      { value: "PENDING", label: "Chờ xử lý" },
      { value: "PROCESSING", label: "Đang chuẩn bị" },
      { value: "READY", label: "Sẵn sàng" },
    ],
  },
  {
    key: "toolingStatus",
    label: "Trạng thái Khuôn",
    type: "combobox-multi",
    options: [
      { value: "PENDING", label: "Chờ xử lý" },
      { value: "PROCESSING", label: "Đang chuẩn bị" },
      { value: "READY", label: "Sẵn sàng" },
    ],
  },
];

export default function F0TrackingBoard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = useRoleAccess();

  // Fix default status to only show active POs
  const {
    items: rawItems,
    paginatedItems: rawPaginated,
    isLoading,
    searchQuery,
    filterValues,
    serverParams,
    showFilter,
    currentPage,
    pageSize,
    totalItems,
    statusCounts: serverStatusCounts,
    statusFilter,
    onStatusChange,
    onFilterChange,
    onPageChange,
    onPageSizeChange,
    onToggleFilter,
    onClearAll,
  } = useServerListFilter<any>({
    queryKey: "production-orders-tracking",
    queryFn: (params) => trpcClient.productionOrders.list.query({
        ...params,
        // Only fetch active status by default if not set
        status: params.status || ["PLANNED", "READY_FOR_SCHEDULING", "IN_PROGRESS"],
    }) as any,
  });

  const allOrders = useMemo(
    () => rawItems.map(mapProductionOrderListItem),
    [rawItems],
  );
  const paginatedItems = useMemo(
    () => rawPaginated.map(mapProductionOrderListItem),
    [rawPaginated],
  );

  const { handleExport, isExporting } = useExcelExport({
    columns: EXPORT_COLUMNS,
    filename: "theo_doi_tien_do_f0",
    sheetName: "Tiến Độ F0",
    queryFn: (params) =>
      trpcClient.productionOrders.list.query({
        ...params,
        status: params.status || ["PLANNED", "READY_FOR_SCHEDULING", "IN_PROGRESS"],
      }),
    currentFilters: serverParams,
    transformData: (items) => (items || []).map(mapProductionOrderListItem),
  });

  const filterTags = React.useMemo(() => {
    return STATUS_ITEMS.map((item) => {
      const sc = serverStatusCounts?.find(
        (s: any) =>
          s.status?.toString()?.toUpperCase() === item.key?.toUpperCase(),
      );
      return {
        ...item,
        count: sc ? sc.count : 0,
      };
    });
  }, [serverStatusCounts]);

  const rowIds = useMemo(() => paginatedItems.map((o: any) => o.id as string), [paginatedItems]);
  const rowSelection = useRowSelection(rowIds);
  const { selectedIds, selectedCount, clearSelection } = rowSelection;

  // Calculate progress safely
  const calculateProgress = (actual: number, plan: number) => {
    if (!plan) return 0;
    const progress = Math.min(Math.round((actual / plan) * 100), 100);
    return progress;
  };

  return (
    <>
    <ListPageShell
      headerFilters={
        <PageFilterTags
          items={filterTags}
          selectedKeys={
            statusFilter
              ? Array.isArray(statusFilter)
                ? statusFilter
                : [statusFilter]
              : []
          }
          onSelect={onStatusChange}
        />
      }
      unitLabel="LSX"
      availableViews={["table"]}
      activeView={"table"}
      onViewChange={() => {}}
      actions={
        <ExportImportButtons
          onExport={() => handleExport()}
          exportLoading={isExporting}
          onDownloadTemplate={() =>
            downloadExcelTemplate(EXPORT_COLUMNS, "theo_doi_f0")
          }
        />
      }
      filterFields={FILTER_FIELDS}
      filterValues={filterValues}
      showFilter={showFilter}
      onToggleFilter={onToggleFilter}
      onClearAll={onClearAll}
      currentPage={currentPage}
      pageSize={pageSize}
      totalItems={totalItems}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
    >
      <ResponsiveDataView
        items={paginatedItems}
        emptyState={
          searchQuery
            ? "Không tìm thấy lệnh SX phù hợp"
            : "Chưa có lệnh sản xuất nào đang chạy"
        }
        renderMobileCard={(order: any) => (
          <div
            className="flex flex-col bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden active:scale-[0.99] transition-transform cursor-pointer"
            onClick={() =>
              navigate({
                to: "/production-orders/$orderId",
                params: { orderId: order.orderId } as any,
              })
            }
          >
            <div className="flex items-center justify-between p-3 border-b border-slate-100 bg-slate-50/50">
              <span className="font-mono text-sm font-bold text-indigo-700">
                {order.lsxId || order.orderId}
              </span>
              <StatusBadge
                type="ProductionOrderStatus"
                value={order.status}
                className="text-[10px] px-2 py-0.5 min-h-0 h-5 border-none"
              />
            </div>

            <div className="p-3 flex flex-col gap-3">
              <div>
                <div className="font-semibold text-slate-900 leading-tight">
                  {order.productName || "Sản phẩm chưa cập nhật"}
                </div>
                <div className="text-xs text-slate-500 mt-1 flex justify-between">
                  <span>SL: <strong>{order.quantity?.toLocaleString("vi-VN")}</strong></span>
                  {order.qtyPlan && <span>ĐM: <strong>{order.qtyPlan.toLocaleString("vi-VN")} tờ</strong></span>}
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-2 text-xs">
                 <div className="flex flex-col gap-1">
                    <span className="text-slate-500 font-medium">Hạn giao</span>
                    <span className={`font-medium ${order.deadline && isBefore(new Date(order.deadline), new Date()) && order.status !== 'COMPLETED' ? 'text-red-600' : 'text-slate-700'}`}>
                      {order.deadline ? format(new Date(order.deadline), 'dd/MM/yyyy') : '—'}
                    </span>
                 </div>
                 <div className="flex flex-col gap-1">
                    <span className="text-slate-500 font-medium">Vật tư</span>
                    <StatusBadge type="ReadinessStatus" value={order.materialStatus || "PENDING"} />
                 </div>
                 <div className="flex flex-col gap-1">
                    <span className="text-slate-500 font-medium">Khuôn</span>
                    <StatusBadge type="ReadinessStatus" value={order.toolingStatus || "PENDING"} />
                 </div>
              </div>
              
              <div className="flex flex-col gap-1 mt-2">
                 <div className="flex justify-between text-xs">
                   <span className="text-slate-600 font-medium">Tiến độ SX</span>
                   <span className="text-indigo-600 font-bold">{calculateProgress(order.actualQuantity, order.quantity)}%</span>
                 </div>
                 <Progress value={calculateProgress(order.actualQuantity, order.quantity)} className="h-2" />
              </div>
            </div>
          </div>
        )}
      >
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="text-xs text-slate-500 bg-slate-50/80 border-b border-slate-200 uppercase">
                  <tr>
                    <DataTableSelectAllCheckbox rowSelection={rowSelection} />
                    <th className="px-4 py-3 font-medium whitespace-nowrap min-w-[120px]">
                      Mã LSX
                    </th>
                    <th className="px-4 py-3 font-medium min-w-[200px]">
                      Sản phẩm
                    </th>
                    <th className="px-4 py-3 font-medium text-right min-w-[140px]">
                      Thành phẩm / Mức
                    </th>
                    <th className="px-4 py-3 font-medium min-w-[120px] text-center">
                      Vật tư
                    </th>
                    <th className="px-4 py-3 font-medium min-w-[120px] text-center">
                      Khuôn
                    </th>
                    <th className="px-4 py-3 font-medium min-w-[120px] text-center">
                      Bình file
                    </th>
                    <th className="px-4 py-3 font-medium min-w-[120px] text-center">
                      Hạn hoàn thành
                    </th>
                    <th className="px-4 py-3 font-medium min-w-[150px]">
                      Tiến độ SX
                    </th>
                    <th className="px-4 py-3 font-medium min-w-[120px] text-center">
                      Trạng thái
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {paginatedItems.map((order: any) => (
                    <DataTableRow
                      key={order.id}
                      id={order.id}
                      rowSelection={rowSelection}
                      className="hover:bg-slate-50/80 transition-colors cursor-pointer group"
                      onClick={() =>
                        navigate({
                          to: "/production-orders/$orderId",
                          params: { orderId: order.orderId } as any,
                        })
                      }
                    >
                      <DataTableRowCheckbox id={order.id} rowSelection={rowSelection} />
                      <td className="px-4 py-3">
                        <span className="font-mono font-semibold text-indigo-600 group-hover:text-indigo-700">
                          {order.lsxId || order.orderId}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-medium text-slate-700">
                        {order.productName || "—"}
                        <div className="text-xs text-slate-500 font-normal mt-0.5">
                          KH: {order.customerName || "—"}
                        </div>
                      </td>
                      
                      <td className="px-4 py-3 text-right tabular-nums">
                        <div className="font-medium text-slate-900">{order.quantity?.toLocaleString("vi-VN")}</div>
                        {order.qtyPlan && (
                          <div className="text-xs text-slate-500 mt-0.5" title="Định mức tờ in / Bù hao">
                            ĐM: {order.qtyPlan.toLocaleString("vi-VN")} tờ
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <StatusBadge
                          type="ReadinessStatus"
                          value={order.materialStatus || "PENDING"}
                        />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <StatusBadge
                          type="ReadinessStatus"
                          value={order.toolingStatus || "PENDING"}
                        />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <StatusBadge
                          type="ReadinessStatus"
                          value={order.prepressStatus || "PENDING"}
                        />
                      </td>
                      <td className="px-4 py-3 text-center">
                        {(() => {
                          if (!order.deadline) return <span className="text-slate-400 text-xs">-</span>;
                          const deadlineDate = new Date(order.deadline);
                          const isOverdue = isBefore(deadlineDate, new Date()) && order.status !== 'COMPLETED';
                          const isNear = isBefore(deadlineDate, addDays(new Date(), 2)) && !isOverdue && order.status !== 'COMPLETED';
                          return (
                            <div className={`text-xs font-medium ${isOverdue ? 'text-red-600' : isNear ? 'text-orange-500' : 'text-slate-600'}`}>
                              {format(deadlineDate, 'dd/MM/yyyy')}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1.5 w-full max-w-[120px]">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-slate-500">
                              {order.actualQuantity?.toLocaleString("vi-VN") || 0}
                            </span>
                            <span className="font-semibold text-indigo-700">
                              {calculateProgress(order.actualQuantity, order.quantity)}%
                            </span>
                          </div>
                          <Progress 
                            value={calculateProgress(order.actualQuantity, order.quantity)} 
                            className="h-1.5" 
                          />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <StatusBadge
                          type="ProductionOrderStatus"
                          value={order.status}
                        />
                      </td>
                    </DataTableRow>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </ResponsiveDataView>
      </ListPageShell>
      <BulkActionBar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        actions={[
          {
            label: "Xuất Excel",
            icon: <Download className="h-3.5 w-3.5" />,
            onClick: () =>
              exportToExcel(
                paginatedItems.filter((o: any) => selectedIds.has(o.id)),
                EXPORT_COLUMNS,
                "theo_doi_tien_do_f0",
                { sheetName: "Tiến Độ F0" },
              ),
          },
        ]}
      />
    </>
  );
}
